import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Modal.css";
import "./GroupsDialog.css";
import "./TravelersDialog.css";
import AlertModal from "./AlertModal";
import AttachmentsList from "./AttachmentsList";
import Common from "../../Common/Common";
import DataBase from "../../dataBase/liquidGuides";

const DOC_TYPES = ["Passport", "Visa", "Insurance", "Vaccination", "ID", "Other"];

const docIcon = (type) => {
  switch (type) {
    case "Passport": return solid("passport");
    case "Visa": return solid("stamp");
    case "Insurance": return solid("file-shield");
    case "Vaccination": return solid("syringe");
    case "ID": return solid("id-card");
    default: return solid("file-lines");
  }
};

const emptyDoc = () => ({
  id: `d_${Date.now()}`,
  type: "Passport",
  label: "",
  country: "",
  issueDate: "",
  expiryDate: "",
  notes: "",
});

const jsDate = (yyyyMmDd) => (yyyyMmDd ? new Date(yyyyMmDd + "T00:00:00") : null);
const toDateString = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Per-traveler document CRUD, opened from TravelersDialog. Structurally
// mirrors GroupsDialog.js's shell (grpdlg-* classes) but scoped to a single
// traveler via props instead of window.guide_obj.
const TravelerDocumentsDialog = ({ show, onHide, traveler, onChange, user_info, Authorization }) => {
  const [formShow, setFormShow] = useState(false);
  const [formDoc, setFormDoc] = useState(emptyDoc());
  const [editingId, setEditingId] = useState(null);

  const [alertShow, setAlertShow] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertButtons, setAlertButtons] = useState([]);

  if (!traveler) return null;
  const documents = traveler.documents || [];

  const openAddForm = () => {
    setEditingId(null);
    setFormDoc(emptyDoc());
    setFormShow(true);
  };

  const openEditForm = (doc) => {
    setEditingId(doc.id);
    setFormDoc({ ...doc });
    setFormShow(true);
  };

  const saveForm = () => {
    if (!formDoc.label.trim() && !formDoc.type) return;
    const updatedDocs = editingId
      ? documents.map((d) => (d.id === editingId ? { ...formDoc } : d))
      : [...documents, { ...formDoc }];
    onChange({ ...traveler, documents: updatedDocs });
    setFormShow(false);
  };

  // Attachments can be uploaded before the document itself is ever saved
  // (see AttachmentsList wiring below), so cancelling a brand-new,
  // never-saved document needs to clean up any orphaned attachment records
  // rather than leaving them stranded under a context nothing points to.
  // Editing an existing (already-persisted) document never triggers this —
  // its attachments legitimately belong to that record either way.
  const deleteAttachmentsForContext = (context) => {
    DataBase.getAttachments(context, user_info.uid, (res) => {
      if (!res.pass) return;
      res.res.forEach((att) => {
        const urls = [att.data.url, att.data.url_m].filter(Boolean);
        DataBase.deleteFromStorage(urls, () => {
          DataBase.deleteAttachment(att.id, () => {});
        });
      });
    });
  };

  const cleanupOrphanAttachments = () => {
    if (formShow && !editingId) {
      deleteAttachmentsForContext(`traveler_document:${traveler.id}:${formDoc.id}`);
    }
  };

  const cancelForm = () => {
    cleanupOrphanAttachments();
    setFormShow(false);
  };

  const handleDialogHide = () => {
    cleanupOrphanAttachments();
    onHide();
  };

  const deleteDoc = (doc) => {
    setAlertTitle("Confirmation");
    setAlertMessage("Confirm Delete Document? Any attached files will be deleted too.");
    setAlertButtons([
      {
        Title: "OK",
        Method: () => {
          deleteAttachmentsForContext(`traveler_document:${traveler.id}:${doc.id}`);
          onChange({ ...traveler, documents: documents.filter((d) => d.id !== doc.id) });
        },
      },
      { Title: "CANCEL", Method: null },
    ]);
    setAlertShow(true);
  };

  const statusClass = (doc) => {
    const status = Common.getDocumentExpiryStatus(doc.expiryDate);
    if (status === "expired") return "td-dot-red";
    if (status === "soon") return "td-dot-amber";
    return "td-dot-green";
  };

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={handleDialogHide}
        dialogClassName="grpdlg-dialog"
        aria-labelledby="contained-modal-title-vcenter"
        centered={false}
        scrollable={false}
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="grpdlg-shell">
            <div className="grpdlg-header">
              <div className="grpdlg-header-icon">
                <FontAwesomeIcon icon={solid("passport")} />
              </div>
              <h2 className="grpdlg-title">{traveler.name}</h2>
              <button className="grpdlg-close" onClick={handleDialogHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="grpdlg-search-row">
              <div className="td-doc-count">
                {documents.length} {documents.length === 1 ? "document" : "documents"}
              </div>
              <button className="grpdlg-add-btn" title="Add document" onClick={openAddForm}>
                <FontAwesomeIcon icon={solid("plus")} />
              </button>
            </div>

            <div className="grpdlg-list">
              {documents.length === 0 && (
                <div className="td-empty">No documents yet. Add a passport, visa, or other travel document.</div>
              )}
              {documents.map((doc) => (
                <div className="grpdlg-item" key={doc.id}>
                  <div className="grpdlg-item-thumb" onClick={() => openEditForm(doc)}>
                    <FontAwesomeIcon icon={docIcon(doc.type)} />
                  </div>
                  <div className="grpdlg-item-info" onClick={() => openEditForm(doc)} style={{ cursor: "pointer" }}>
                    <span className="grpdlg-item-label">{doc.label ? doc.label : doc.type}</span>
                    <span className="grpdlg-item-schedule">
                      {doc.type}{doc.country ? ` · ${doc.country}` : ""}
                      {doc.expiryDate ? ` · Expires ${doc.expiryDate}` : ""}
                    </span>
                  </div>
                  <span className={`td-dot ${statusClass(doc)}`} title={Common.getDocumentExpiryStatus(doc.expiryDate)} />
                  <div className="grpdlg-item-actions">
                    <button className="grpdlg-icon-btn" title="Edit" onClick={() => openEditForm(doc)}>
                      <FontAwesomeIcon icon={solid("pen")} />
                    </button>
                    <button className="grpdlg-icon-btn danger" title="Delete" onClick={() => deleteDoc(doc)}>
                      <FontAwesomeIcon icon={solid("trash")} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {formShow && (
              <div className="td-form">
                <div className="td-form-row">
                  <label>Type</label>
                  <select
                    className="form-control"
                    value={formDoc.type}
                    onChange={(e) => setFormDoc({ ...formDoc, type: e.target.value })}
                  >
                    {DOC_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="td-form-row">
                  <label>Label</label>
                  <input
                    className="form-control"
                    placeholder="e.g. US Passport"
                    value={formDoc.label}
                    onChange={(e) => setFormDoc({ ...formDoc, label: e.target.value })}
                  />
                </div>
                <div className="td-form-row">
                  <label>Country</label>
                  <input
                    className="form-control"
                    placeholder="e.g. United States"
                    value={formDoc.country}
                    onChange={(e) => setFormDoc({ ...formDoc, country: e.target.value })}
                  />
                </div>
                <div className="td-form-row-pair">
                  <div className="td-form-row">
                    <label>Issue date</label>
                    <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                      selected={jsDate(formDoc.issueDate)}
                      onChange={(date) => setFormDoc({ ...formDoc, issueDate: toDateString(date) })}
                      dateFormat="MMM d, yyyy"
                      withPortal
                      className="form-control"
                    />
                  </div>
                  <div className="td-form-row">
                    <label>Expiry date</label>
                    <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                      selected={jsDate(formDoc.expiryDate)}
                      onChange={(date) => setFormDoc({ ...formDoc, expiryDate: toDateString(date) })}
                      dateFormat="MMM d, yyyy"
                      withPortal
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="td-form-row">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formDoc.notes}
                    onChange={(e) => setFormDoc({ ...formDoc, notes: e.target.value })}
                  />
                </div>
                <AttachmentsList
                  context={`traveler_document:${traveler.id}:${formDoc.id}`}
                  user_info={user_info}
                  Authorization={Authorization}
                />

                <div className="td-form-actions">
                  <button className="btn btn-secondary btn-sm" onClick={cancelForm}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveForm}>Save</button>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <AlertModal
        show={alertShow}
        onHide={() => setAlertShow(false)}
        AlertModalTitle={alertTitle}
        AlertModalMessage={alertMessage}
        AlertModalButtons={alertButtons}
      />
    </div>
  );
};

export default TravelerDocumentsDialog;
